/**
 * useAnalytics — PostHog wrapper
 * --------------------------------
 * Thin composable over posthog-js so components never import PostHog directly.
 * All calls are no-ops in dev (PostHog is only initialised in production).
 *
 * Usage:
 *   const { track, identify, reset } = useAnalytics();
 *   track('lesson_started', { lessonId, lessonTitle });
 *   identify(uid, { email, plan });
 *   reset(); // call on logout
 */

import posthog from 'posthog-js';

export function useAnalytics() {
  /**
   * Fire a custom event.
   * @param {string} event  - snake_case event name, e.g. 'lesson_completed'
   * @param {object} props  - optional properties
   */
  function track(event, props = {}) {
    posthog.capture(event, props);
  }

  /**
   * Identify the logged-in user so events are linked to their profile.
   * Call after login / on app boot when session is available.
   * @param {string} uid   - unique user ID (Firebase uid)
   * @param {object} traits - e.g. { email, plan, name }
   */
  function identify(uid, traits = {}) {
    posthog.identify(uid, traits);
  }

  /**
   * Reset the PostHog identity — call on logout so subsequent events
   * are not attributed to the previous user.
   */
  function reset() {
    posthog.reset();
  }

  return { track, identify, reset };
}
